import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { setPageMeta } from '../lib/setPageMeta';
export function Story() {
  const {
    t
  } = useLanguage();
  const chapters = t('storyPage.chapters') as any[];
  useEffect(() => {
    setPageMeta({
      title: 'From zero to billions',
      description: 'The origin story behind Dance for Fans — how creator-driven platforms went from a family-funded bet to a global powerhouse.'
    });
  }, []);
  return <div className="min-h-screen font-sans" style={{
    backgroundColor: '#0a0f1f',
    color: '#f5e9d0'
  }} data-id="element-512">
      {/* Ambient lighting */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" data-id="element-513">
        <motion.div className="absolute top-[-20%] left-[10%] w-[700px] h-[700px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(217, 168, 81, 0.10)'
      }} animate={{
        scale: [1, 1.15, 1],
        x: [0, 40, 0]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-514" />
        <motion.div className="absolute bottom-[-20%] right-[5%] w-[600px] h-[600px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(56, 89, 156, 0.18)'
      }} animate={{
        scale: [1, 1.2, 1],
        y: [0, -50, 0]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-515" />
      </div>

      <div className="relative z-10" data-id="element-516">
        {/* Top bar */}
        <header className="px-6 py-6 border-b" style={{
        borderColor: 'rgba(245, 233, 208, 0.1)'
      }} data-id="element-517">
          <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-518">
            <Link to="/" className="inline-flex items-center gap-2 text-sm hover:opacity-80 transition-opacity" style={{
            color: '#f5e9d0'
          }} data-id="element-519">
              <ArrowLeft className="w-4 h-4" data-id="element-520" />
              Back to danceforfans.com
            </Link>
            <span className="text-xs uppercase tracking-[0.3em]" style={{
            color: '#d9a851'
          }} data-id="element-521">
              The Archive
            </span>
          </div>
        </header>

        {/* Hero */}
        <section className="px-6 py-24 md:py-32" data-id="element-522">
          <div className="max-w-4xl mx-auto text-center" data-id="element-523">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="inline-flex items-center gap-2 mb-8" data-id="element-524">
              <BookOpen className="w-4 h-4" style={{
              color: '#d9a851'
            }} data-id="element-525" />
              <span className="text-sm uppercase tracking-[0.4em]" style={{
              color: '#d9a851'
            }} data-id="element-526">
                Chapter zero
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-serif italic text-5xl md:text-7xl lg:text-8xl leading-[1.05] mb-8" style={{
            color: '#f5e9d0'
          }} data-id="element-527">
              From zero to{' '}
              <span style={{
              color: '#d9a851'
            }} data-id="element-528">
                billions
              </span>
              .
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed" style={{
            color: 'rgba(245, 233, 208, 0.7)'
          }} data-id="element-529">
              {t('storyPage.heroDescStart')}
              <span style={{
              color: '#f5e9d0',
              fontWeight: 500
            }} data-id="element-530">
                {t('storyPage.heroDescHighlight')}
              </span>
              {t('storyPage.heroDescEnd')}
            </motion.p>
          </div>
        </section>

        {/* Chapters */}
        <section className="px-6 pb-16" data-id="element-531">
          <div className="max-w-3xl mx-auto space-y-12" data-id="element-532">
            {chapters.map((c, i) => <motion.article key={c.year} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.7,
            delay: i * 0.05
          }} className="grid md:grid-cols-[140px_1fr] gap-6 md:gap-10" data-id="element-533">
                <div data-id="element-534">
                  <p className="font-serif text-3xl md:text-4xl" style={{
                color: '#d9a851'
              }} data-id="element-535">
                    {c.year}
                  </p>
                  <div className="mt-3 h-px w-12" style={{
                backgroundColor: 'rgba(217, 168, 81, 0.4)'
              }} data-id="element-536" />
                </div>
                <div data-id="element-537">
                  <h2 className="font-serif text-2xl md:text-3xl mb-4 leading-snug" style={{
                color: '#f5e9d0'
              }} data-id="element-538">
                    {c.title}
                  </h2>
                  <p className="leading-relaxed text-base md:text-lg" style={{
                color: 'rgba(245, 233, 208, 0.75)'
              }} data-id="element-539">
                    {c.body}
                  </p>
                </div>
              </motion.article>)}
          </div>
        </section>

        {/* Summary */}
        <section className="px-6 py-20" data-id="element-540">
          <div className="max-w-3xl mx-auto" data-id="element-541">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="rounded-3xl p-10 md:p-14 text-center border" style={{
            backgroundColor: 'rgba(20, 30, 60, 0.5)',
            borderColor: 'rgba(217, 168, 81, 0.3)'
          }} data-id="element-542">
              <p className="text-xs uppercase tracking-[0.4em] mb-6" style={{
              color: '#d9a851'
            }} data-id="element-543">
                {t('storyPage.summaryTag')}
              </p>
              <p className="font-serif italic text-2xl md:text-3xl leading-snug" style={{
              color: '#f5e9d0'
            }} data-id="element-544">
                {t('storyPage.summaryText')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-10 border-t" style={{
        borderColor: 'rgba(245, 233, 208, 0.1)'
      }} data-id="element-545">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" data-id="element-546">
            <Link to="/" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity" style={{
            color: '#f5e9d0'
          }} data-id="element-547">
              <ArrowLeft className="w-4 h-4" data-id="element-548" />
              {t('common.backToMainStage')}
            </Link>
            <span style={{
            color: 'rgba(245, 233, 208, 0.5)'
          }} data-id="element-549">
              {t('storyPage.footerTag')}
            </span>
          </div>
        </footer>
      </div>
    </div>;
}